import { useMemo, useState, useRef, useEffect } from 'react';
import { Button } from '@bigcommerce/big-design';
import { PriceFormatter } from './Sections/ItemsInOrderSection/Columns';
import {
	DiagramColumn,
	RowNumberColumn,
} from './Sections/ItemsInOrderSection/Columns';
import Table from '../../../components/Table';
import { useClient } from '../../../context/client';
import { useAsyncEffect } from '../../../hooks';
import { createDefaultRenderer } from '../../../utils';
import { useCurrency, useOrderProducts } from '../context';
import { addProducts } from '../store/orderProducts';

const rowNumberMap = ['first', 'second', 'third', 'fourth'];

function ActionColumn(product) {
	const [productsInOrder, dispatchOrderProducts] = useOrderProducts();
	const productIsInOrder = productsInOrder.some(
		({ combined_id }) => product.combined_id === combined_id,
	);

	return (
		<Button
			variant={productIsInOrder ? 'primary' : 'secondary'}
			disabled={productIsInOrder}
			onClick={() => dispatchOrderProducts(addProducts(product))}
		>
			{productIsInOrder ? 'Added!' : 'Add to Order'}
		</Button>
	);
}

function PriceColumn(product) {
	const [currency] = useCurrency();

	return useMemo(() => {
		const currencyFormatter = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency,
			minimumFractionDigits: 2,
		});
		if (
			product.price &&
			product.sale_price &&
			product.price !== product.sale_price
		) {
			return (
				<PriceFormatter
					salePrice={product.sale_price}
					originalPrice={product.price}
					formatter={currencyFormatter}
				/>
			);
		}

		return currencyFormatter.format(product.price);
	}, [product.price, product.sale_price, currency]);
}

function useParentClassName(className, isActive) {
	const elementRef = useRef(null);

	useEffect(() => {
		if (elementRef.current === null) return;

		const parentNode = elementRef.current.parentNode.parentNode;
		isActive
			? parentNode.classList.add(className)
			: parentNode.classList.remove(className);
	}, [className, isActive, elementRef.current]);

	return elementRef;
}

function withParentClassName(Component) {
	return function ComponentWithParentClassName(props) {
		const elementRef = useParentClassName(
			'bottomBorder',
			props.isLastVendorRow,
		);

		return <Component {...props} ref={elementRef} />;
	};
}

const tableColumns = [
	{ hash: 'diagram', header: '', render: withParentClassName(DiagramColumn) },
	// Addons weren't implemented at time of refactor
	// { hash: "addons", header: "", render: ({value}) => value },
	{ hash: 'row', header: 'Row', render: RowNumberColumn },
	{
		hash: 'description',
		header: 'Description',
		render: createDefaultRenderer('description'),
	},
	{ hash: 'notes', header: 'Notes', render: createDefaultRenderer('notes') },
	{
		hash: 'vendor',
		header: 'Vendor',
		render: createDefaultRenderer('vendor'),
	},
	{
		hash: 'part_number',
		header: 'Part Number',
		render: createDefaultRenderer('part_number'),
	},
	{ hash: 'price', header: 'Price', render: PriceColumn },
	{ hash: 'action', header: '', render: ActionColumn },
];

function useProductsWithPricing(products, currency, customerGroup) {
	const [productPricing, setProductPricing] = useState({});
	const client = useClient();

	useAsyncEffect(async () => {
		if (products.length === 0) {
			setProductPricing({});
			return;
		}

		const bodyData = {
			itemData: products.map((product) => ({
				product_id: product.product_id,
				variant_id: product.selectedVariant?.id,
			})),
			currency,
			customerGroup,
		};

		const prices = await client.getPrices(bodyData);

		// create object from prices, keyed by variant id.
		setProductPricing(
			Object.fromEntries(
				prices
					.filter((item) => item.variant_id)
					.map((item) => [item.variant_id, item]),
			),
		);
	}, [products, currency, customerGroup]);

	return useMemo(
		() =>
			products.map((product) => {
				const price =
					productPricing[product.selectedVariant?.id]?.price
						?.as_entered || product.selectedVariant?.price;

				const sale_price =
					productPricing[product.selectedVariant?.id]?.sale_price
						?.as_entered || product.selectedVariant?.sale_price;

				return {
					...product,
					price: price || 'Loading...',
					sale_price: sale_price,
				};
			}),
		[products, productPricing],
	);
}

function getVendorMap(products) {
	const vendorArray = products.map((product) => product.vendor);

	return [...new Set(vendorArray)];
}

function useProductsSortedByRow(products) {
	return useMemo(() => {
		const vendorMap = getVendorMap(products);
		const sortedUniqueVendors = vendorMap.sort();
		const sortedTableRows = products.sort(
			(a, b) =>
				sortedUniqueVendors.indexOf(a.vendor) -
					sortedUniqueVendors.indexOf(b.vendor) ||
				rowNumberMap.indexOf(a.row.toLowerCase()) -
					rowNumberMap.indexOf(b.row.toLowerCase()),
		);

		const lastVendorItemIndexMap = new Map(
			sortedUniqueVendors
				.slice(0, sortedUniqueVendors.length - 1)
				.map((vendor) => [
					vendor,
					sortedTableRows.findLastIndex(
						(row) => row.vendor === vendor,
					),
				]),
		);

		const sortedTableRowsWithLastVendorRow = sortedTableRows.map(
			(row, index) => ({
				...row,
				isLastVendorRow:
					lastVendorItemIndexMap.get(row.vendor) === index,
			}),
		);

		return sortedTableRowsWithLastVendorRow;
	}, [products]);
}

export default function SearchResults({ products, currency, customerGroup }) {
	const productsWithPricing = useProductsWithPricing(
		products,
		currency,
		customerGroup,
	);
	const [, dispatchOrderProducts] = useOrderProducts();

	const [selectedItems, setSelectedItems] = useState([]);

	const sortedProductsWithPricing =
		useProductsSortedByRow(productsWithPricing);

	return (
		<Table
			className="call-center-order-items"
			keyField="id"
			actions={
				<Button
					variant="primary"
					marginLeft="large"
					disabled={selectedItems.length === 0}
					onClick={() => {
						dispatchOrderProducts(addProducts(selectedItems));
						setSelectedItems([]);
					}}
				>
					Add Selected to Order
				</Button>
			}
			columns={tableColumns}
			items={sortedProductsWithPricing}
			selectable={{
				selectedItems,
				onSelectionChange: setSelectedItems,
			}}
		/>
	);
}
